import { useState, useEffect, useCallback } from 'react'
import posthog from 'posthog-js'
import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'
import {
  getFlagOverride,
  syncFlagOverridesFromUrl,
} from '@hapana/shared/web/utils/feature-flag-overrides.utils'

export type FeatureFlagValue = string | number | boolean | null

export interface UseFeatureFlagOptions {
  defaultValue?: FeatureFlagValue
  sendUsageEvent?: boolean
  eventProperties?: Record<string, unknown>
}

export interface UseFeatureFlagReturn {
  value: FeatureFlagValue
  isLoading: boolean
  isReady: boolean
  refresh: () => void
}

export { FeatureFlagKeys }

// Seed the URL overrides exactly once, on the first hook mount rather than at
// module import — avoids an import-time window.location read + localStorage
// write (awkward under HMR/SSR and in tests).
let flagOverridesSeeded = false

// `flagKey` is the flag's VALUE (e.g. `FeatureFlagKeys.ProductTiering` === 'product-tiering') — the
// id PostHog registers and what every caller passes, not the enum key NAME.
export const useFeatureFlag = <T extends FeatureFlagKeys>(
  flagKey: T,
  options: UseFeatureFlagOptions = {}
): UseFeatureFlagReturn => {
  const { defaultValue = null, sendUsageEvent = false, eventProperties = {} } = options

  const [value, setValue] = useState<FeatureFlagValue>(defaultValue)
  const [isLoading, setIsLoading] = useState(true)
  const [isReady, setIsReady] = useState(false)

  const checkFlag = useCallback(() => {
    // Local override wins over PostHog and works even when PostHog never inits.
    const override = getFlagOverride(flagKey)
    if (override !== undefined) {
      setValue(override)
      setIsReady(true)
      setIsLoading(false)
      return
    }

    if (!posthog || typeof posthog.getFeatureFlag !== 'function') {
      setValue(defaultValue)
      setIsLoading(false)
      setIsReady(false)
      return
    }

    try {
      const flagValue = posthog.getFeatureFlag(flagKey)
      setValue(flagValue !== undefined ? flagValue : defaultValue)
      setIsReady(true)

      if (sendUsageEvent && typeof posthog.capture === 'function') {
        posthog.capture('feature_flag_checked', {
          flag_key: flagKey,
          flag_value: flagValue,
          default_value: defaultValue,
          ...eventProperties,
        })
      }
    } catch {
      setValue(defaultValue)
      setIsReady(false)
    } finally {
      setIsLoading(false)
    }
  }, [flagKey, defaultValue, sendUsageEvent, eventProperties])

  const refresh = useCallback(() => {
    setIsLoading(true)
    checkFlag()
  }, [checkFlag])

  useEffect(() => {
    // Seed URL overrides once (before the first resolve) instead of at import.
    if (!flagOverridesSeeded) {
      flagOverridesSeeded = true
      syncFlagOverridesFromUrl()
    }
    // Resolve immediately so local overrides and defaults apply even when PostHog
    // never initializes (e.g. feature envs with no key, where onFeatureFlags never fires).
    checkFlag()

    // Subscribe to flag changes — onFeatureFlags fires once flags are loaded
    // and on every subsequent flag update. Captures unsubscribe for cleanup.
    const unsub = posthog.onFeatureFlags(() => checkFlag())

    return () => {
      unsub?.()
    }
  }, [checkFlag])

  return { value, isLoading, isReady, refresh }
}

/**
 * Fail-closed FE mirror of the backend `isFeatureFlagEnabled` (`shared/backend/observability`):
 * `true` only when PostHog explicitly says the flag is on; off / missing / warming up / unreachable
 * all read `false`. Use this to gate client behaviour on the same flag the backend gates on, so both
 * ends "do nothing when off". Same `=== true` semantics on both sides.
 */
export const useIsFeatureFlagEnabled = (flag: FeatureFlagKeys): boolean =>
  useFeatureFlag(flag, { defaultValue: false }).value === true
